import { InfoCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { ProductDoc, ProductInventoryDoc, ProductInventoryUnit, ProductSupplierDoc } from '@gooduncles/gu-app-schema';
import { Button, Input, Modal, Radio, Select, Space, Spin, Tooltip, message } from 'antd';
import { FC, useEffect, useState } from 'react';

import {
  batchEnd,
  batchStart,
  getProductInventoryId,
  updateProduct,
  updateProductInventory,
} from 'src/utils/firebase/firebase-short-cut';
import { createProductInventoryForInit, getInventoryNameForProduct } from 'src/utils/product-inventory-util';

import { ProductInventoryWithDetail } from 'src/hooks/subscription/useProductInventoriesWithDetail';

interface DisconnectProductModalProps {
  isVisible: boolean;
  onCancel: () => void;
  product: ProductDoc;
  inventory: ProductInventoryDoc;
  unit: ProductInventoryUnit;
  inventories?: ProductInventoryWithDetail[]; // 상위 컴포넌트에서 전달받은 인벤토리 목록
  productSuppliers: ProductSupplierDoc[];
}

const DisconnectProductModal: FC<DisconnectProductModalProps> = ({
  isVisible,
  onCancel,
  product,
  inventory,
  unit,
  inventories = [], // 기본값으로 빈 배열 설정
  productSuppliers,
}) => {
  const [loading, setLoading] = useState(false);
  const [disconnectOption, setDisconnectOption] = useState<'search' | 'create'>('search');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<ProductInventoryWithDetail[]>([]);
  const [selectedInventory, setSelectedInventory] = useState<ProductInventoryWithDetail | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<ProductInventoryUnit>(unit);
  const [newInventoryName, setNewInventoryName] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [allInventories, setAllInventories] = useState<ProductInventoryWithDetail[]>(inventories);

  // 상위 컴포넌트에서 인벤토리 목록이 변경되면 상태 업데이트
  useEffect(() => {
    setAllInventories(inventories);
  }, [inventories]);

  // 인벤토리 검색 함수
  const searchInventory = async () => {
    if (!searchKeyword.trim()) {
      message.warning('검색어를 입력해주세요.');
      return;
    }

    setIsSearching(true);
    try {
      // 클라이언트 측에서 인벤토리 목록을 필터링하여 검색
      const keyword = searchKeyword.toLowerCase();
      const results = allInventories.filter(
        (inv) =>
          // 현재 인벤토리는 검색 결과에서 제외
          inv._id !== inventory._id &&
          // 인벤토리 이름에 키워드가 포함된 경우 검색 결과에 포함
          inv.inventoryName.toLowerCase().includes(keyword),
      );

      // 약간의 지연 시간을 두어 검색 중인 느낌을 줌
      setTimeout(() => {
        setSearchResults(results);
        setIsSearching(false);
      }, 300);
    } catch (error) {
      console.error('인벤토리 검색 실패:', error);
      message.error('인벤토리 검색에 실패했습니다.');
      setIsSearching(false);
    }
  };

  // 인벤토리가 해당 unit을 지원하는지 확인
  const hasUnit = (inv: ProductInventoryDoc, unitToCheck: ProductInventoryUnit = selectedUnit) => {
    return inv.uom && unitToCheck in inv.uom;
  };

  // 인벤토리의 해당 unit에 이미 연결된 상품이 있는지 확인
  const hasProductWithUnit = (
    inventory: ProductInventoryWithDetail,
    unitToCheck: ProductInventoryUnit = selectedUnit,
  ) => {
    // products 속성이 없거나 비어있으면 false 반환
    if (!inventory.products || Object.keys(inventory.products).length === 0) return false;

    // 해당 인벤토리의 모든 상품을 확인
    for (const productId in inventory.products) {
      const productInfo = inventory.products[productId];
      // 해당 unit을 사용하는 상품이 있는지 확인
      if (productInfo.inventoryUnit === unitToCheck) {
        return true;
      }
    }
    return false;
  };

  // 인벤토리 선택 가능 여부 확인
  const canSelectInventory = (inv: ProductInventoryWithDetail, unitToCheck: ProductInventoryUnit = selectedUnit) => {
    // 해당 unit을 지원하는지 확인
    const unitSupported = hasUnit(inv, unitToCheck);
    // 해당 unit에 이미 연결된 상품이 있는지 확인
    const unitOccupied = hasProductWithUnit(inv, unitToCheck);

    return unitSupported && !unitOccupied;
  };

  // 인벤토리 선택 불가 이유 반환
  const getDisabledReason = (inv: ProductInventoryWithDetail, unitToCheck: ProductInventoryUnit = selectedUnit) => {
    if (!hasUnit(inv, unitToCheck)) {
      return `이 인벤토리는 ${unitToCheck.toUpperCase()} 단위를 지원하지 않습니다.`;
    }
    if (hasProductWithUnit(inv, unitToCheck)) {
      return `이 인벤토리의 ${unitToCheck.toUpperCase()} 단위에는 이미 연결된 상품이 있습니다.`;
    }
    return '';
  };

  // 모달이 열릴 때 새 인벤토리 이름 초기화
  useEffect(() => {
    if (isVisible) {
      // 새 인벤토리 이름을 상품 정보를 기반으로 생성
      setNewInventoryName(getInventoryNameForProduct(product));

      // 약간의 지연 시간을 두어 모달이 완전히 열린 후 포커스
      const timer = setTimeout(() => {
        const inputElement = document.querySelector('.inventory-search-input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isVisible, product]);

  // 선택한 인벤토리로 상품 연결 변경
  const changeInventory = async () => {
    setLoading(true);
    try {
      batchStart();
      if (disconnectOption === 'search') {
        if (!selectedInventory) {
          message.error('인벤토리를 선택해주세요.');
          setLoading(false);
          return;
        }

        // 선택한 인벤토리가 조건을 만족하는지 확인
        if (!canSelectInventory(selectedInventory, selectedUnit)) {
          message.error(getDisabledReason(selectedInventory, selectedUnit));
          setLoading(false);
          return;
        }

        await updateProductInventory(
          selectedInventory._id,
          {
            [`uom.${selectedUnit}.cost`]: product.cost,
          },
          { bBatch: true },
        );
        await updateProduct(
          product.productId,
          {
            inventoryId: selectedInventory._id,
            inventoryUnit: selectedUnit, // 최종 결정된 단위 사용
          },
          { bBatch: true },
        );
      }
      // 새 인벤토리 생성
      else {
        if (!newInventoryName.trim()) {
          message.error('새 인벤토리 이름을 입력해주세요.');
          setLoading(false);
          return;
        }

        if (!selectedSupplierId) {
          message.error('거래처를 선택해주세요.');
          setLoading(false);
          return;
        }

        const newInventoryId = getProductInventoryId();
        // 선택한 거래처 ID 전달
        await createProductInventoryForInit(newInventoryId, product, newInventoryName, selectedSupplierId);
        await updateProduct(
          product.productId,
          {
            inventoryId: newInventoryId,
            inventoryUnit: ProductInventoryUnit.EA, // 최종 결정된 단위 사용
          },
          { bBatch: true },
        );
      }

      await batchEnd();

      message.success('상품 연결이 성공적으로 변경되었습니다.');
      onCancel();
    } catch (error) {
      console.error('상품 연결 변경 실패:', error);
      message.error('상품 연결 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모달이 닫힐 때 상태 초기화
  const handleCancel = () => {
    setDisconnectOption('search');
    setSearchKeyword('');
    setSearchResults([]);
    setSelectedInventory(null);
    setSelectedUnit(unit);
    setNewInventoryName('');
    setSelectedSupplierId('');
    onCancel();
  };

  // 모달이 열릴 때마다 검색어 입력 필드에 포커스
  useEffect(() => {
    if (isVisible) {
      // 약간의 지연 시간을 두어 모달이 완전히 열린 후 포커스
      const timer = setTimeout(() => {
        const inputElement = document.querySelector('.inventory-search-input') as HTMLInputElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  return (
    <Modal
      title='상품 연결 변경'
      open={isVisible}
      onCancel={handleCancel}
      footer={[
        <Button key='cancel' onClick={handleCancel}>
          취소
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={loading}
          onClick={changeInventory}
          disabled={
            (disconnectOption === 'search' &&
              (!selectedInventory || !canSelectInventory(selectedInventory, selectedUnit))) ||
            (disconnectOption === 'create' && (!newInventoryName.trim() || !selectedSupplierId))
          }
        >
          연결 변경하기
        </Button>,
      ]}
      width={600}
    >
      <Space direction='vertical' style={{ width: '100%' }}>
        <p>
          상품 &apos;{product.fullName}&apos;의 연결을 변경합니다.
          <br />
          다른 인벤토리로 연결하거나 새 인벤토리를 생성할 수 있습니다.
        </p>

        <Radio.Group
          value={disconnectOption}
          onChange={(e) => setDisconnectOption(e.target.value)}
          style={{ marginBottom: '16px' }}
        >
          <Radio value='search'>기존 인벤토리 검색</Radio>
          <Radio value='create'>새 인벤토리 생성</Radio>
        </Radio.Group>

        {disconnectOption === 'search' ? (
          <div>
            <Space style={{ marginBottom: '16px', width: '100%' }}>
              <Input
                className='inventory-search-input'
                placeholder='인벤토리 이름 검색'
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onPressEnter={searchInventory}
                style={{ flex: 1 }}
              />
              <Button type='primary' icon={<SearchOutlined />} onClick={searchInventory} loading={isSearching}>
                검색
              </Button>
            </Space>

            <div style={{ marginBottom: '16px' }}>
              <p style={{ marginBottom: '8px' }}>연결할 단위 선택:</p>
              <Radio.Group value={selectedUnit} onChange={(e) => setSelectedUnit(e.target.value)}>
                <Radio value='ea'>Ea (개)</Radio>
                <Radio value='bundle'>Bundle (묶음)</Radio>
              </Radio.Group>
            </div>

            {isSearching ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
              </div>
            ) : searchResults.length > 0 ? (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                {searchResults.map((inv) => {
                  const isSelectable = canSelectInventory(inv, selectedUnit);
                  const disabledReason = isSelectable ? '' : getDisabledReason(inv, selectedUnit);

                  return (
                    <div
                      key={inv._id}
                      style={{
                        padding: '8px 12px',
                        cursor: isSelectable ? 'pointer' : 'not-allowed',
                        backgroundColor: selectedInventory?._id === inv._id ? '#e6f7ff' : 'transparent',
                        borderBottom: '1px solid #f0f0f0',
                        opacity: isSelectable ? 1 : 0.6,
                      }}
                      onClick={() => isSelectable && setSelectedInventory(inv)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <p style={{ margin: 0 }}>
                            <strong>{inv.inventoryName}</strong>
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>ID: {inv._id}</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                            지원 단위:{' '}
                            {inv.uom
                              ? Object.keys(inv.uom)
                                  .map((u) => u.toUpperCase())
                                  .join(', ')
                              : '없음'}
                          </p>
                        </div>
                        {!isSelectable && (
                          <Tooltip title={disabledReason}>
                            <InfoCircleOutlined style={{ color: '#ff4d4f' }} />
                          </Tooltip>
                        )}
                      </div>
                      {!isSelectable && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ff4d4f' }}>{disabledReason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : searchKeyword ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#888' }}>검색 결과가 없습니다.</div>
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#888' }}>인벤토리 이름을 검색해주세요.</div>
            )}
          </div>
        ) : (
          <div>
            <Input
              placeholder='새 인벤토리 이름 입력'
              value={newInventoryName}
              onChange={(e) => setNewInventoryName(e.target.value)}
              prefix={<PlusOutlined />}
              style={{ marginBottom: '16px' }}
            />

            {/* 거래처 선택 드롭다운 추가 */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ marginBottom: '8px' }}>거래처 선택:</p>
              <Select
                placeholder='거래처를 선택해주세요'
                style={{ width: '100%' }}
                value={selectedSupplierId || undefined}
                onChange={(value) => setSelectedSupplierId(value)}
                loading={productSuppliers.length === 0}
                showSearch
                optionFilterProp='children'
                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                options={productSuppliers?.map((pSupplier) => ({
                  value: pSupplier._id,
                  label: pSupplier.name,
                }))}
              />
            </div>

            <p style={{ color: '#888', fontSize: '12px' }}>
              새 인벤토리를 생성하고 이 상품을 연결합니다. 새 인벤토리에는 Ea 단위가 자동으로 추가됩니다.
            </p>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default DisconnectProductModal;
